"use client";
import React from 'react';
import { X } from 'lucide-react';

function MeasurementModal({ isOpen, onClose, type }) {
  if (!isOpen) return null;

  const measurementData = {
    petticoat: {
      title: "Petticoat Measurements",
      measurements: [
        { size: "XS", waist: "26-28", length: "38", hip: "32-34" },
        { size: "S", waist: "28-30", length: "38", hip: "34-36" },
        { size: "M", waist: "30-32", length: "38", hip: "36-38" },
        { size: "L", waist: "32-34", length: "38", hip: "38-40" },
        { size: "XL", waist: "34-36", length: "38", hip: "40-42" },
        { size: "XXL", waist: "36-38", length: "38", hip: "42-44" }
      ],
      headers: ["Size", "Waist (inches)", "Length (inches)", "Hip (inches)"]
    },
    blouse: {
      title: "Blouse Measurements",
      measurements: [
        { size: "XS", bust: "32-34", waist: "26-28", shoulder: "13.5", length: "15" },
        { size: "S", bust: "34-36", waist: "28-30", shoulder: "14", length: "15.5" },
        { size: "M", bust: "36-38", waist: "30-32", shoulder: "14.5", length: "16" },
        { size: "L", bust: "38-40", waist: "32-34", shoulder: "15", length: "16.5" },
        { size: "XL", bust: "40-42", waist: "34-36", shoulder: "15.5", length: "17" },
        { size: "XXL", bust: "42-44", waist: "36-38", shoulder: "16", length: "17.5" }
      ],
      headers: ["Size", "Bust (inches)", "Waist (inches)", "Shoulder (inches)", "Length (inches)"]
    }
  };

  const data = measurementData[type] || measurementData.petticoat;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-900">
            {data.title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-600 mb-4">
              Please refer to the size chart below to select the perfect fit. All measurements are in inches.
            </p>
          </div>

          {/* Size Chart Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  {data.headers.map((header, index) => (
                    <th key={index} className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-900">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.measurements.map((measurement, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2 font-medium">{measurement.size}</td>
                    {Object.entries(measurement).slice(1).map(([key, value], idx) => (
                      <td key={idx} className="border border-gray-300 px-4 py-2">{value}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Additional Info */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Measurement Tips:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Measure yourself wearing the undergarments you plan to wear with the outfit</li>
              <li>• Keep the measuring tape snug but not tight</li>
              <li>• For best results, have someone else take your measurements</li>
              <li>• If you're between sizes, we recommend choosing the larger size</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default MeasurementModal;
